import {ContentTileWithTitle} from '#/atoms/content-tile';
import {VerticalSeparator} from '#/atoms/vertical-separator';
import {ActionMenuOverviewInfoRow} from '#/components/actionmenu/action-menu-overview';
import {TransactionVariables} from '#/components/actionmenu/sections/transaction/transaction-flow-data';
import {ClassName, DefaultChildren} from '#/utils/utils';
import {ComponentProps, FunctionComponent} from 'react';
import {useTranslation} from 'react-i18next';
import {TransactionType} from 'shareland-common';

export const ActionMenuContentSectionOverview: FunctionComponent<
  DefaultChildren & Partial<ComponentProps<typeof ContentTileWithTitle>>
> = ({children, ...rest}) => {
  const {t} = useTranslation('translation', {
    keyPrefix: 'pages.actionMenu.information.sections.information.contentSection.overview',
  });

  return (
    <ContentTileWithTitle {...rest} title={t('title')} contentClassName='text-white space-y-2'>
      {children}
    </ContentTileWithTitle>
  );
};

export const ActionMenuContentSectionOverviewBuy: FunctionComponent<{
  transactionVars: TransactionVariables[TransactionType.Purchase];
  setMaxSlippagePercentage?: React.Dispatch<React.SetStateAction<number>>;
}> = ({transactionVars, setMaxSlippagePercentage}) => {
  return (
    <ActionMenuContentSectionOverview>
      <ActionMenuOverviewInfoRow.MarketPrice />
      <ActionMenuOverviewInfoRow.EstimatedPricePerToken
        landTokenAmount={transactionVars.landTokenAmount}
        stableTokenAmount={transactionVars.stableTokenAmount}
      />
      <ActionMenuOverviewInfoRow.TransactionFee transactionFee={transactionVars.transactionFee} />
      <ActionMenuOverviewInfoRow.Total total={transactionVars.totalStableAmount} />
      <VerticalSeparator />
      {setMaxSlippagePercentage ? (
        <ActionMenuOverviewInfoRow.MaxSlippageInput
          maxSlippagePercentage={transactionVars.maxSlippagePercentage}
          setMaxSlippagePercentage={setMaxSlippagePercentage}
        />
      ) : (
        <ActionMenuOverviewInfoRow.MaxSlippage maxSlippage={transactionVars.maxSlippagePercentage} />
      )}
      <ActionMenuOverviewInfoRow.MinReceivedNative landTokenAmount={transactionVars.minReceivedLandAmount} />
    </ActionMenuContentSectionOverview>
  );
};

export const ActionMenuContentSectionOverviewSell: FunctionComponent<
  {
    transactionVars: TransactionVariables[TransactionType.Sell];
    setMaxSlippagePercentage?: React.Dispatch<React.SetStateAction<number>>;
  } & ClassName
> = ({className, transactionVars, setMaxSlippagePercentage}) => {
  return (
    <ActionMenuContentSectionOverview className={className}>
      <ActionMenuOverviewInfoRow.MarketPrice />
      <ActionMenuOverviewInfoRow.EstimatedPricePerToken
        landTokenAmount={transactionVars.landTokenAmount}
        stableTokenAmount={transactionVars.stableTokenAmount}
      />
      <ActionMenuOverviewInfoRow.TransactionFee transactionFee={transactionVars.transactionFee} />
      <ActionMenuOverviewInfoRow.Receive receive={transactionVars.receiveStableAmount} />
      <VerticalSeparator />
      {setMaxSlippagePercentage ? (
        <ActionMenuOverviewInfoRow.MaxSlippageInput
          maxSlippagePercentage={transactionVars.maxSlippagePercentage}
          setMaxSlippagePercentage={setMaxSlippagePercentage}
        />
      ) : (
        <ActionMenuOverviewInfoRow.MaxSlippage maxSlippage={transactionVars.maxSlippagePercentage} />
      )}
      <ActionMenuOverviewInfoRow.MinReceivedStable stableTokenAmount={transactionVars.minReceivedStableAmountWithFee} />
    </ActionMenuContentSectionOverview>
  );
};

export const ActionMenuContentSectionOverviewLimitBuy: FunctionComponent<
  {
    transactionVars: TransactionVariables[TransactionType.PurchaseLimitOrder];
  } & ClassName
> = ({className, transactionVars}) => {
  return (
    <ActionMenuContentSectionOverview className={className}>
      <ActionMenuOverviewInfoRow.TransactionFee
        transactionFee={transactionVars.transactionFee + transactionVars.purchaseTransactionFee}
      />
      <ActionMenuOverviewInfoRow.Total total={transactionVars.totalStableAmount} />

      <VerticalSeparator />
      <ActionMenuOverviewInfoRow.MarketPrice />
      <ActionMenuOverviewInfoRow.AveragePrice targetPrice={transactionVars.averagePrice} />
      <ActionMenuOverviewInfoRow.MarketPriceDifference targetPrice={transactionVars.averagePrice} />
    </ActionMenuContentSectionOverview>
  );
};

export const ActionMenuContentSectionOverviewLimitSell: FunctionComponent<
  {
    transactionVars: TransactionVariables[TransactionType.SellLimitOrder];
  } & ClassName
> = ({className, transactionVars}) => {
  return (
    <ActionMenuContentSectionOverview className={className}>
      <ActionMenuOverviewInfoRow.MarketPrice />
      <ActionMenuOverviewInfoRow.TransactionFee
        transactionFee={transactionVars.transactionFee + transactionVars.sellTransactionFee}
      />
      <ActionMenuOverviewInfoRow.Receive receive={transactionVars.receiveStableAmount} />
      <VerticalSeparator />
      <ActionMenuOverviewInfoRow.AveragePrice targetPrice={transactionVars.targetPrice} />
      <ActionMenuOverviewInfoRow.MarketPriceDifference targetPrice={transactionVars.targetPrice} />
    </ActionMenuContentSectionOverview>
  );
};
